import { Box, Typography } from "@mui/material";
import { PlanMedicationHeader } from "./header";
import { PlanMedicationTitle } from "./title";
import { PlanMedicationNavbar } from "./navbar";
import { GroupCard } from "./group-card";
import { useState } from "react";
import { semanticColors } from "../../utils/colors";
import { typography } from "../../utils/typography";
import { useTranslation } from "react-i18next";

const dummyPatient = {
  name: "João Paulo Silva",
  age: "68",
  updatedBy: "Dr. Ricardo Pimenta Araújo"
};

const dummyGroups = [
  {
    id: "g1",
    name: "Paracetamol",
    route: "Via oral",
    medications: [
      {
        id: 1,
        name: "Paracetamol",
        tradeName: "Ben-U-Ron",
        route: "Via oral",
        dosage: "500mg",
        quantityPerPackage: 60,
        unit: "comprimidos",
        prescriber: "Dr.ª José Silva",
        nextToma: "Pequeno-almoço",
        stockCount: 23,
        stockDuration: "12 dias",
        stockBreakDate: "03/03/2026",
        packageContent: "Cada embalagem contém 60 comprimidos",
        startDate: "28/02/2026",
        endDate: "17/04/2026",
        treatmentDuration: "48 dias",
        doses: [
          { dose: "2 comprimidos", frequency: "Pequeno-almoço e jantar" },
          { dose: "1/2 comprimido", frequency: "Almoço" }
        ],
        prescription: {
          validUntil: "03/09/2026",
          status: "Válida",
          toDispense: "5 embalagens",
          totalPackages: 8,
          duration: "180 dias",
          guideLink: true
        }
      },
      {
        id: 2,
        name: "Brufen",
        tradeName: "Ibuprofeno",
        route: "Via oral",
        dosage: "400mg",
        quantityPerPackage: 30,
        unit: "comprimidos",
        prescriber: "Dr.ª José Silva",
        nextToma: "Jantar",
        stockCount: 10,
        stockDuration: "10 dias",
        stockBreakDate: "15/04/2026",
        packageContent: "Cada embalagem contém 30 comprimidos",
        startDate: "01/03/2026",
        endDate: "30/04/2026",
        treatmentDuration: "60 dias",
        doses: [{ dose: "1 comprimido", frequency: "Jantar" }],
        prescription: {
          validUntil: "01/09/2026",
          status: "Válida",
          toDispense: "3 embalagens",
          totalPackages: 4,
          duration: "180 dias",
          guideLink: false
        }
      }
    ]
  },
  {
    id: "g2",
    name: "Omeprazol",
    route: "Via oral",
    medications: [
      {
        id: 3,
        name: "Omeprazol",
        tradeName: "Ben-U-Ron",
        route: "Via oral",
        dosage: "20mg",
        quantityPerPackage: 14,
        unit: "capsulas",
        prescriber: "Dr.ª Fernanda Batista",
        nextToma: "Almoço",
        stockCount: 0,
        stockDuration: "0 dias",
        stockBreakDate: "20/03/2026",
        packageContent: "Cada embalagem contém 14 cápsulas",
        startDate: "01/02/2026",
        endDate: "30/06/2026",
        treatmentDuration: "150 dias",
        doses: [
          { dose: "1 cápsula", frequency: "Em jejum, 30 min antes do almoço" }
        ],
        prescription: {
          validUntil: "01/08/2026",
          status: "Válida",
          toDispense: "8 embalagens",
          totalPackages: 10,
          duration: "180 dias",
          guideLink: false
        }
      }
    ]
  },
  {
    id: "g3",
    name: "Salbutamol",
    route: "Via inalatória",
    medications: [
      {
        id: 4,
        name: "Salbutamol",
        tradeName: "Ventilan",
        route: "Via inalatória",
        dosage: "100mcg",
        quantityPerPackage: 200,
        unit: "doses",
        prescriber: "Dr. Manuel Santos",
        nextToma: "Conforme necessário",
        stockCount: 5,
        stockDuration: "Variável",
        stockBreakDate: null,
        packageContent: "Cada inalador contém 200 doses",
        startDate: "15/01/2026",
        endDate: null,
        treatmentDuration: "Contínuo",
        doses: [
          {
            dose: "1-2 inalações",
            frequency: "Conforme necessário (máx 8/dia)"
          }
        ],
        prescription: {
          validUntil: "15/07/2026",
          status: "Válida",
          toDispense: "2 inaladores",
          totalPackages: 3,
          duration: "180 dias",
          guideLink: false
        }
      }
    ]
  }
];

const dummyHistoryGroups = [
  {
    id: "h1",
    name: "Amoxicilina",
    route: "Via oral",
    medications: [
      {
        id: 101,
        name: "Amoxicilina",
        tradeName: "Clamoxyl",
        route: "Via oral",
        dosage: "500mg",
        quantityPerPackage: 20,
        unit: "cápsulas",
        prescriber: "Dr.ª José Silva",
        nextToma: null,
        stockCount: 0,
        stockDuration: "0 dias",
        stockBreakDate: "15/01/2026",
        packageContent: "Cada embalagem contém 20 cápsulas",
        startDate: "01/01/2026",
        endDate: "14/01/2026",
        treatmentDuration: "14 dias",
        doses: [{ dose: "1 cápsula", frequency: "3 vezes ao dia" }],
        prescription: {
          validUntil: "01/04/2026",
          status: "Concluída",
          toDispense: "0 embalagens",
          totalPackages: 2,
          duration: "90 dias",
          guideLink: false
        }
      }
    ]
  },
  {
    id: "h2",
    name: "Ibuprofeno",
    route: "Via oral",
    medications: [
      {
        id: 102,
        name: "Ibuprofeno",
        tradeName: "Advil",
        route: "Via oral",
        dosage: "600mg",
        quantityPerPackage: 20,
        unit: "comprimidos",
        prescriber: "Dr. Manuel Santos",
        nextToma: null,
        stockCount: 0,
        stockDuration: "0 dias",
        stockBreakDate: "10/02/2026",
        packageContent: "Cada embalagem contém 20 comprimidos",
        startDate: "01/02/2026",
        endDate: "10/02/2026",
        treatmentDuration: "10 dias",
        doses: [{ dose: "1 comprimido", frequency: "De 8 em 8 horas" }],
        prescription: {
          validUntil: "01/05/2026",
          status: "Expirada",
          toDispense: "0 embalagens",
          totalPackages: 1,
          duration: "90 dias",
          guideLink: false
        }
      }
    ]
  }
];

export function PlanMedication() {
  const [activeTab, setActiveTab] = useState("active");
  const { t } = useTranslation();

  const displayGroups =
    activeTab === "active" ? dummyGroups : dummyHistoryGroups;

  return (
    <Box
      sx={{
        width: "100%",
        bgcolor: semanticColors.surface.containerLow,
        minHeight: "100vh"
      }}
    >
      <PlanMedicationNavbar patient={dummyPatient} />

      <Box
        sx={{
          pt: "146px",
          pb: "80px",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: "40px"
        }}
      >
        {/* Patient info — narrow section */}
        <Box
          sx={{
            px: { xs: "16px", sm: "24px" },
            maxWidth: 880,
            width: "100%"
          }}
        >
          <PlanMedicationHeader patient={dummyPatient} />
        </Box>

        {/* Medication section — wider */}
        <Box
          sx={{
            width: "100%",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: "32px"
          }}
        >
          <PlanMedicationTitle
            activeTab={activeTab}
            onTabChange={setActiveTab}
          />

          {/* Cards — 1200px wide */}
          <Box
            sx={{
              px: { xs: "16px", sm: "24px" },
              maxWidth: 1200,
              width: "100%",
              display: "flex",
              flexDirection: "column",
              gap: "24px"
            }}
          >
            {displayGroups.map(group => (
              <GroupCard key={group.id} group={group} />
            ))}

            <Box
              sx={{
                maxWidth: 832,
                mx: "auto",
                width: "100%",
                p: "24px 40px",
                bgcolor: "rgba(59, 130, 246, 0.1)",
                border: `1px solid ${semanticColors.status.processing}`,
                borderRadius: "16px"
              }}
            >
              <Typography sx={{ ...typography.body.xl, color: "#1E2939" }}>
                {t("planMedication.infoBox")}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
